import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaUpload, 
    FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaExchangeAlt, 
    FaFolder, FaSave, FaYoutube, FaFilePdf
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import './AddGallery.css';
import axiosInstance from '../../api/axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

// Strict Predefined Folders
const PREDEFINED_FOLDERS = ['PHOTOS', 'VIDEOS', 'BOOKS'];

// --- ADD/EDIT ITEM MODAL ---
const GalleryFormModal = ({ isOpen, onClose, data, mode, onSave }) => {
    const [formData, setFormData] = useState({ folderName: 'PHOTOS', title: '', ytLink: '', isActive: true });
    const [imageFile, setImageFile] = useState(null);
    const [pdfFile, setPdfFile] = useState(null);
    const [preview, setPreview] = useState('');
    
    const fileInputRef = useRef(null);
    const pdfInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            if (data) {
                setFormData({
                    folderName: data.folderName || 'PHOTOS',
                    title: data.title || '',
                    ytLink: data.ytLink || '',
                    isActive: data.isActive,
                });
                setPreview(data.image ? `${API_BASE_URL}${data.image}` : '');
                setImageFile(null);
                setPdfFile(null);
            } else {
                setFormData({ folderName: 'PHOTOS', title: '', ytLink: '', isActive: true });
                setPreview('');
                setImageFile(null);
                setPdfFile(null);
            }
        }
    }, [data, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'folderName') {
                updated.ytLink = '';
                setImageFile(null);
                setPdfFile(null);
                setPreview('');
            }
            return updated;
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handlePdfChange = (e) => {
        const file = e.target.files[0];
        if (file) setPdfFile(file);
    };

    const handleRemoveMedia = (e) => {
        e.stopPropagation();
        setImageFile(null);
        setPreview('');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = () => {
        if (!formData.title) return Swal.fire('Error', 'Please enter a title', 'error');

        const submissionData = new FormData();
        submissionData.append('folderName', formData.folderName);
        submissionData.append('title', formData.title);
        submissionData.append('isActive', formData.isActive.toString());

        if (formData.folderName === 'VIDEOS') {
            if (!formData.ytLink) return Swal.fire('Error', 'Please provide a YouTube Link', 'error');
            submissionData.append('ytLink', formData.ytLink);
        } else {
            if (mode === 'add' && !imageFile && formData.folderName !== 'VIDEOS') {
                return Swal.fire('Error', 'Please upload an image/cover photo', 'error');
            }
            if (imageFile) submissionData.append('image', imageFile);
        }

        if (formData.folderName === 'BOOKS') {
            if (mode === 'add' && !pdfFile) return Swal.fire('Error', 'Please upload a PDF file for the book', 'error');
            if (pdfFile) submissionData.append('pdf', pdfFile);
        }

        onSave(submissionData);
    };

    if (!isOpen) return null;

    return (
        <div className="admin-add-galary-page-modal-overlay">
            <div className="admin-add-galary-page-modal-content" onClick={e => e.stopPropagation()}>
                <div className="admin-add-galary-page-modal-header">
                    <h2>{data ? 'Edit Gallery Item' : 'Add Gallery Item'}</h2>
                    <button className="admin-add-galary-page-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="admin-add-galary-page-modal-body single-column">
                    
                    <div className="admin-add-galary-page-form-group">
                        <label>Category</label>
                        <select name="folderName" value={formData.folderName} onChange={handleChange} className="admin-add-galary-page-select-input">
                            {PREDEFINED_FOLDERS.map((f, i) => <option key={i} value={f}>{f}</option>)}
                        </select>
                    </div>

                    <div className="admin-add-galary-page-form-group">
                        <label>Title</label>
                        <input type="text" name="title" value={formData.title} onChange={handleChange} className="admin-add-galary-page-input" placeholder="Enter title..." />
                    </div>

                    {formData.folderName === 'VIDEOS' ? (
                        <div className="admin-add-galary-page-form-group">
                            <label>YouTube Link</label>
                            <input type="url" name="ytLink" value={formData.ytLink} onChange={handleChange} className="admin-add-galary-page-input" placeholder="https://youtube.com/..." />
                        </div>
                    ) : (
                        <div className="admin-add-galary-page-form-group">
                            <label>{formData.folderName === 'BOOKS' ? 'Cover Photo' : 'Image'}</label>
                            <div className="admin-add-galary-page-preview-box large" onClick={() => fileInputRef.current.click()}>
                                {preview ? (
                                    <div className="admin-add-galary-page-image-container">
                                        <img src={preview} alt="Cover" />
                                        <div className="admin-add-galary-page-overlay">
                                            <button className="admin-add-galary-page-mini-btn" onClick={(e) => {e.stopPropagation(); fileInputRef.current.click();}}><FaExchangeAlt /></button>
                                            <button className="admin-add-galary-page-mini-btn remove" onClick={handleRemoveMedia}><FaTrashAlt /></button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="admin-add-galary-page-upload-placeholder"><FaUpload size={28}/><span>Click to Upload Photo</span></div>
                                )}
                                <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} accept="image/*" />
                            </div>
                        </div>
                    )}

                    {formData.folderName === 'BOOKS' && (
                        <div className="admin-add-galary-page-form-group">
                            <label>Upload PDF Book</label>
                            <div className="admin-add-galary-page-pdf-upload-box">
                                <input type="file" ref={pdfInputRef} onChange={handlePdfChange} accept="application/pdf" className="admin-add-galary-page-input" />
                                {data?.pdf && !pdfFile && <small style={{color: '#3498db', marginTop: '5px', display: 'block'}}>Current PDF exists. Uploading new replaces it.</small>}
                            </div>
                        </div>
                    )}

                </div>
                <div className="admin-add-galary-page-modal-footer">
                    <button className="admin-add-galary-page-btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="admin-add-galary-page-btn-save" onClick={handleSubmit}>Save Item</button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const AddGallery = () => {
    const [galleryData, setGalleryData] = useState([]); 
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedItem, setSelectedItem] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    const fetchGalleryData = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/gallery');
            const data = response.data;
            if (Array.isArray(data)) setGalleryData(data);
            else setGalleryData([]);
        } catch (error) {
            console.error("Error fetching gallery:", error);
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Failed to load gallery data', 'error');
            }
            setGalleryData([]);
        }
    }, []);

    useEffect(() => { fetchGalleryData(); }, [fetchGalleryData]);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    
    const filteredData = useMemo(() => {
        return galleryData.filter(item => {
            const term = searchTerm.toLowerCase();
            const fName = item.folderName ? item.folderName.toLowerCase() : 'n/a';
            const tName = item.title ? item.title.toLowerCase() : '';
            return (fName.includes(term) || tName.includes(term));
        });
    }, [galleryData, searchTerm]);

    const totalResults = filteredData.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startResult = totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalResults);
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    const handleSaveItem = async (formData) => {
        try {
            if (modalMode === 'edit') {
                await axiosInstance.put(`/api/gallery/${selectedItem._id}`, formData);
            } else {
                await axiosInstance.post('/api/gallery', formData);
            }

            await fetchGalleryData();
            Swal.fire(modalMode === 'edit' ? 'Updated!' : 'Added!', 'Operation successful.', 'success');
            setIsItemModalOpen(false);
        } catch (error) {
            console.error("Save Error:", error);
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Server error occurred', 'error');
            }
        }
    };

    const handleDeleteItem = (id) => {
        Swal.fire({ title: 'Delete Item?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/gallery/${id}`);
                    await fetchGalleryData();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    if (error.response?.status !== 401) {
                        Swal.fire('Error', 'Could not delete item.', 'error');
                    }
                }
            }
        });
    };

    const toggleActive = async (id, currentStatus) => {
        try {
            await axiosInstance.put(`/api/gallery/${id}`, { isActive: !currentStatus });
            await fetchGalleryData();
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Could not update status.', 'error');
            }
        }
    };

    const openItemModal = (mode, item = null) => {
        setModalMode(mode);
        setSelectedItem(item);
        setIsItemModalOpen(true);
    };

    return (
        <>
        <Helmet>
                    <title>Gallery | Rajput Shiksha Kosh</title>
            
                    <meta
                      name="description"
                      content="Manage Gallery Items for the Rajput Shiksha Kosh organization."
                    />
                  </Helmet>
        <div className="admin-add-galary-page-wrapper">
            <div className="admin-add-galary-page-container">
                <GalleryFormModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} data={selectedItem} mode={modalMode} onSave={handleSaveItem} />
                
                <h2 className="admin-add-galary-page-title">Gallery Management</h2>

                <div className="admin-add-galary-page-controls">
                    <div className="admin-add-galary-page-search-wrapper">
                        <FaSearch className="admin-add-galary-page-search-icon" />
                        <input type="text" placeholder="Search category or title..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="admin-add-galary-page-search-input" />
                    </div>
                    <div className="admin-add-galary-page-action-group">
                        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="admin-add-galary-page-limit-select">
                            <option value="10">10 / page</option>
                            <option value="20">20 / page</option>
                            <option value="30">30 / page</option>
                            <option value="50">50 / page</option>
                        </select>
                        <button className="admin-add-galary-page-add-btn" onClick={() => openItemModal('add')}>
                            <FaPlus /> Add Gallery Item
                        </button>
                    </div>
                </div>

                <div className="admin-add-galary-page-table-card">
                    <div className="admin-add-galary-page-table-wrapper">
                        <table className="admin-add-galary-page-table">
                            <thead>
                                <tr>
                                    <th className="admin-add-galary-page-col-sno">S.No</th>
                                    <th className="admin-add-galary-page-col-name">Category</th>
                                    <th className="admin-add-galary-page-col-image">Media</th>
                                    <th className="admin-add-galary-page-col-desc">Title</th>
                                    <th className="admin-add-galary-page-col-created">Created At</th>
                                    <th className="admin-add-galary-page-col-active">Status</th>
                                    <th className="admin-add-galary-page-col-actions">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.length === 0 ? (
                                    <tr><td colSpan="7" className="admin-add-galary-page-no-data">No gallery items found.</td></tr>
                                ) : (
                                    paginatedData.map((item, index) => (
                                    <React.Fragment key={item._id}>
                                        <tr className="admin-add-galary-page-row">
                                            <td className="admin-add-galary-page-col-sno">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td className="admin-add-galary-page-col-name">
                                                <div className="admin-add-galary-page-name-wrapper">
                                                    {item.folderName ? <><FaFolder className="folder-icon"/> {item.folderName}</> : <span>N/A</span>}
                                                    <button className="admin-add-galary-page-mobile-toggle-btn" onClick={() => toggleRow(item._id)}>{expandedRow === item._id ? <FaChevronUp /> : <FaChevronDown />}</button>
                                                </div>
                                            </td>
                                            <td className="admin-add-galary-page-col-image">
                                                {item.folderName === 'VIDEOS' ? (
                                                    <a href={item.ytLink} target="_blank" rel="noreferrer" className="admin-add-galary-page-link-badge yt"><FaYoutube /> Watch</a>
                                                ) : (
                                                    <div className="admin-add-galary-page-table-thumb">
                                                        <img src={`${API_BASE_URL}${item.image}`} alt="Media" onError={(e) => e.target.src = 'https://via.placeholder.com/50'} />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="admin-add-galary-page-col-desc">
                                                <div className="admin-add-galary-page-truncate">
                                                    {item.title}
                                                    {item.folderName === 'BOOKS' && item.pdf && (
                                                        <a href={`${API_BASE_URL}${item.pdf}`} target="_blank" rel="noreferrer" className="admin-add-galary-page-link-badge pdf" style={{marginLeft:'10px'}}><FaFilePdf/> PDF</a>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="admin-add-galary-page-col-created">{formatDate(item.createdAt)}</td>
                                            <td className="admin-add-galary-page-col-active">
                                                <label className="admin-add-galary-page-switch">
                                                    <input type="checkbox" checked={item.isActive !== false} onChange={() => toggleActive(item._id, item.isActive !== false)} />
                                                    <span className="admin-add-galary-page-slider-toggle" />
                                                </label>
                                                <div className={`admin-add-galary-page-status-text ${item.isActive !== false ? 'active' : 'disabled'}`}>
                                                    {item.isActive !== false ? 'Active' : 'Disabled'}
                                                </div>
                                            </td>
                                            <td className="admin-add-galary-page-col-actions">
                                                <div className="admin-add-galary-page-action-icons">
                                                    <button className="admin-add-galary-page-icon-btn edit" onClick={() => openItemModal('edit', item)}><FaEdit /></button>
                                                    <button className="admin-add-galary-page-icon-btn delete" onClick={() => handleDeleteItem(item._id)}><FaTrashAlt /></button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedRow === item._id && (
                                            <tr className="admin-add-galary-page-details-row">
                                                <td colSpan="7">
                                                    <div className="admin-add-galary-page-details-content">
                                                         <div className="admin-add-galary-page-details-grid column-wise">
                                                            <div className="admin-add-galary-page-detail-card dc-image">
                                                                <div className="admin-add-galary-page-detail-label">Media</div>
                                                                {item.folderName === 'VIDEOS' ? (
                                                                     <a href={item.ytLink} target="_blank" rel="noreferrer" style={{color:'#e74c3c'}}><FaYoutube size={24}/> Watch Video</a>
                                                                ) : (
                                                                    <div className="admin-add-galary-page-detail-thumb">
                                                                        <img src={`${API_BASE_URL}${item.image}`} alt="Cover" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="admin-add-galary-page-detail-card dc-desc">
                                                                <div className="admin-add-galary-page-detail-label">Title & File</div>
                                                                <div className="admin-add-galary-page-detail-text">{item.title}</div>
                                                                {item.folderName === 'BOOKS' && item.pdf && (
                                                                    <a href={`${API_BASE_URL}${item.pdf}`} target="_blank" rel="noreferrer" style={{marginTop:'10px', display:'inline-block', color:'#3498db', fontWeight: '600'}}><FaFilePdf/> View PDF Document</a>
                                                                )}
                                                            </div>
                                                            <div className="admin-add-galary-page-detail-card dc-created">
                                                                <div className="admin-add-galary-page-detail-label">Created At</div>
                                                                <div className="admin-add-galary-page-detail-text">{formatDate(item.createdAt)}</div>
                                                            </div>
                                                            <div className="admin-add-galary-page-detail-card dc-active">
                                                                <div className="admin-add-galary-page-detail-label">Status</div>
                                                                <label className="admin-add-galary-page-switch">
                                                                    <input type="checkbox" checked={item.isActive !== false} onChange={() => toggleActive(item._id, item.isActive !== false)} />
                                                                    <span className="admin-add-galary-page-slider-toggle" />
                                                                </label>
                                                            </div>
                                                            <div className="admin-add-galary-page-detail-card dc-actions">
                                                                <div className="admin-add-galary-page-detail-label">Actions</div>
                                                                <div className="admin-add-galary-page-action-icons" style={{ justifyContent: 'flex-start' }}>
                                                                    <button className="admin-add-galary-page-icon-btn edit" onClick={() => openItemModal('edit', item)}><FaEdit /> Edit</button>
                                                                    <button className="admin-add-galary-page-icon-btn delete" onClick={() => handleDeleteItem(item._id)}><FaTrashAlt /> Delete</button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {paginatedData.length > 0 && (
                    <div className="admin-add-galary-page-pagination-container">
                        <div className="admin-add-galary-page-pagination-info">Showing {startResult} to {endResult} of {totalResults} results</div>
                        <div className="admin-add-galary-page-pagination-controls">
                            <button className="admin-add-galary-page-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                            <span className="admin-add-galary-page-page-count">Page {currentPage} of {totalPages || 1}</span>
                            <button className="admin-add-galary-page-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default AddGallery;